import { V2_FOOD_ICON_KEYS } from "./v2Schema";
import { V2_MAIN_FOOD_CATEGORIES } from "./v2FoodData";

export const FOOD_FILTERS = Object.freeze({
  all: "all",
  verified: "verified",
  custom: "custom",
});

export const FOOD_LOG_CATEGORY_FILTERS = Object.freeze(buildFoodLogCategoryFilters());

export const INVALID_QUANTITY_UNIT_MESSAGE =
  "Quantity unit must be a valid unit, such as g, ml, cup, plate, serving, or piece.";

export const INVALID_CUSTOM_FOOD_QUANTITY_UNIT_MESSAGE =
  "Quantity unit must be selected from the available options.";

export const CUSTOM_FOOD_QUANTITY_UNITS = Object.freeze([
  "g",
  "ml",
  "cup",
  "plate",
  "serving",
  "piece",
  "bowl",
  "tbsp",
  "tsp",
]);

const allowedIconKeys = new Set(Object.values(V2_FOOD_ICON_KEYS));
const allowedCustomFoodUnits = new Set(CUSTOM_FOOD_QUANTITY_UNITS);
const allowedSimpleUnits = new Set([
  "g",
  "gram",
  "grams",
  "mg",
  "ml",
  "l",
  "cup",
  "cups",
  "plate",
  "plates",
  "serving",
  "servings",
  "portion",
  "portions",
  "piece",
  "pieces",
  "tbsp",
  "tablespoon",
  "tablespoons",
  "tsp",
  "teaspoon",
  "teaspoons",
  "bowl",
  "bowls",
  "tablet",
  "tablets",
  "medium apple",
  "cup cooked",
]);

export function toFoodNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function getFoodIconKey(iconKey) {
  return allowedIconKeys.has(iconKey) ? iconKey : V2_FOOD_ICON_KEYS.default;
}

export function isValidQuantityUnit(unit) {
  const normalized = normalizeQuantityUnit(unit);
  if (!normalized) return false;
  if (/^\d+(\.\d+)?$/.test(normalized)) return false;
  if (!/[a-z]/i.test(normalized)) return false;
  if (allowedSimpleUnits.has(normalized.toLowerCase())) return true;
  return /^[a-z][a-z\s/-]{0,39}$/i.test(normalized);
}

export function isAllowedCustomFoodQuantityUnit(unit) {
  return allowedCustomFoodUnits.has(normalizeQuantityUnit(unit).toLowerCase());
}

export function getSafeQuantityUnit(unit, fallback = "serving") {
  const normalized = normalizeQuantityUnit(unit);
  return isValidQuantityUnit(normalized) ? normalized : fallback;
}

export function formatServing(quantity, unit, fallback = "serving") {
  return `${formatFoodAmount(quantity)} ${getSafeQuantityUnit(unit, fallback)}`;
}

export function formatFoodServing(food = {}) {
  const servingLabel = String(food.serving_label || food.serving_size || "").trim();
  return servingLabel || formatServing(food.quantity ?? food.serving_quantity, food.quantity_unit ?? food.serving_unit);
}

export function getLocalizedFoodName(food = {}, lang = "en") {
  const arabicName = String(food.display_name_ar || food.name_ar || "").trim();
  if (lang === "ar" && arabicName) return arabicName;
  return food.display_name || food.name_en || food.name || "";
}

export function getLocalizedFoodTypeLabel(food = {}, copy = {}) {
  const labels = copy.foodLabels || {};
  if (food.type === "custom") return labels.custom || food.type_label || "Custom Food";
  if (food.type === "verified") return labels.verified || food.type_label || "Verified Food";
  return food.type_label || labels.food || "Food";
}

export function formatFoodAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(1);
}

export function normalizeMainFood(food) {
  return {
    ...food,
    display_name: food.name_en || food.name || "",
    display_name_ar: food.name_ar || "",
    type: "verified",
    type_label: "Verified Food",
    quantity: toFoodNumber(food.serving_quantity),
    quantity_unit: getSafeQuantityUnit(food.serving_unit, "serving"),
    serving_label: String(food.serving_label || food.serving_size || "").trim(),
    icon_key: getFoodIconKey(food.icon_key || V2_FOOD_ICON_KEYS.main),
  };
}

export function normalizeCustomFood(food) {
  return {
    ...food,
    display_name: food.name || food.name_en || "",
    display_name_ar: food.name_ar || "",
    type: "custom",
    type_label: "Custom Food",
    category: food.category || "Custom",
    serving_quantity: toFoodNumber(food.quantity ?? food.serving_quantity),
    serving_unit: getSafeQuantityUnit(food.quantity_unit || food.serving_unit, "serving"),
    quantity: toFoodNumber(food.quantity ?? food.serving_quantity),
    quantity_unit: getSafeQuantityUnit(food.quantity_unit || food.serving_unit, "serving"),
    verified: false,
    source: food.source || "User custom food",
    active: food.active !== false,
    icon_key: getFoodIconKey(food.icon_key || V2_FOOD_ICON_KEYS.custom),
  };
}

export function buildUnifiedFoodList(mainFoods = [], customFoods = []) {
  return [
    ...mainFoods.filter((food) => food.active !== false).map(normalizeMainFood),
    ...customFoods.filter((food) => food.active !== false).map(normalizeCustomFood),
  ];
}

export function getFoodCategories(foods = []) {
  return Array.from(new Set(foods.map((food) => food.category).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );
}

export function filterFoods(foods = [], { search = "", type = FOOD_FILTERS.all, category = "all" } = {}) {
  const query = search.trim().toLowerCase();

  return foods.filter((food) => {
    const matchesType =
      type === FOOD_FILTERS.all ||
      (type === FOOD_FILTERS.verified && food.type === "verified") ||
      (type === FOOD_FILTERS.custom && food.type === "custom");
    const matchesCategory =
      category === "all" ||
      String(food.category || "").trim().toLowerCase() === String(category).trim().toLowerCase();
    const searchable = [food.display_name, food.display_name_ar, food.category, food.source]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchesSearch = !query || searchable.includes(query);

    return matchesType && matchesCategory && matchesSearch;
  });
}

export function getFoodLogCategoryFilters(foods = []) {
  return FOOD_LOG_CATEGORY_FILTERS.map((filter) => ({
    ...filter,
    count: filterFoodsByLogCategory(foods, { categoryKey: filter.key }).length,
  }));
}

export function filterFoodsByLogCategory(
  foods = [],
  { search = "", categoryKey = "all" } = {}
) {
  const selectedFilter =
    FOOD_LOG_CATEGORY_FILTERS.find((filter) => filter.key === categoryKey) ||
    FOOD_LOG_CATEGORY_FILTERS[0];

  return filterFoods(foods, {
    search,
    type: selectedFilter.type || FOOD_FILTERS.all,
    category: selectedFilter.category || "all",
  });
}

export function getFoodLogSearchResults(foods = [], filters = {}) {
  return filterFoodsByLogCategory(foods, filters);
}

export function validateCustomFood(input = {}) {
  const errors = {};

  if (!String(input.name || "").trim()) {
    errors.name = "Food name is required.";
  }

  if (!isAllowedCustomFoodQuantityUnit(input.quantity_unit)) {
    errors.quantity_unit = INVALID_CUSTOM_FOOD_QUANTITY_UNIT_MESSAGE;
  }

  validatePositive(input.quantity, "quantity", "Quantity must be greater than 0.", errors);
  validateZeroOrPositive(input.potassium_mg, "potassium_mg", "Potassium must be 0 or greater.", errors);
  validateZeroOrPositive(input.phosphorus_mg, "phosphorus_mg", "Phosphorus must be 0 or greater.", errors);
  validateZeroOrPositive(input.sodium_mg, "sodium_mg", "Sodium must be 0 or greater.", errors);
  validateZeroOrPositive(input.protein_g, "protein_g", "Protein must be 0 or greater.", errors);

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

export function prepareCustomFoodPayload(input = {}) {
  const validation = validateCustomFood(input);
  if (!validation.valid) {
    return { validation, payload: null };
  }

  const mixerIngredients = Array.isArray(input.mixer_ingredients)
    ? input.mixer_ingredients.map((ingredient) => ({
        id: String(ingredient.id || ""),
        name: String(ingredient.name || "").trim(),
        quantity: toFoodNumber(ingredient.quantity),
        quantity_unit: getSafeQuantityUnit(ingredient.quantity_unit, "serving"),
        potassium_mg: toFoodNumber(ingredient.potassium_mg),
        phosphorus_mg: toFoodNumber(ingredient.phosphorus_mg),
        sodium_mg: toFoodNumber(ingredient.sodium_mg),
        protein_g: toFoodNumber(ingredient.protein_g),
        source: String(ingredient.source || "").trim(),
      }))
    : undefined;

  const payload = {
    name: String(input.name).trim(),
    name_ar: String(input.name_ar || "").trim(),
    category: String(input.category || "Custom").trim() || "Custom",
    quantity: toFoodNumber(input.quantity),
    quantity_unit: getSafeQuantityUnit(input.quantity_unit, "serving"),
    potassium_mg: toFoodNumber(input.potassium_mg),
    phosphorus_mg: toFoodNumber(input.phosphorus_mg),
    sodium_mg: toFoodNumber(input.sodium_mg),
    protein_g: toFoodNumber(input.protein_g),
    icon_key: getFoodIconKey(input.icon_key || V2_FOOD_ICON_KEYS.custom),
    active: true,
    source: String(input.source || "User custom food").trim() || "User custom food",
  };

  if (mixerIngredients) {
    payload.mixer_ingredients = mixerIngredients;
  }

  return {
    validation,
    payload,
  };
}

function normalizeQuantityUnit(unit) {
  return String(unit || "").trim().replace(/\s+/g, " ");
}

function buildFoodLogCategoryFilters(categories = V2_MAIN_FOOD_CATEGORIES) {
  return [
    { key: "all", label: "All" },
    ...categories.map((category) => ({
      key: categoryToFilterKey(category),
      label: category,
      category,
    })),
    { key: "custom", label: "Custom foods", type: FOOD_FILTERS.custom },
  ];
}

function categoryToFilterKey(category) {
  return String(category || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function validatePositive(value, key, message, errors) {
  const parsed = Number(value);
  if (!String(value ?? "").trim() || !Number.isFinite(parsed) || parsed <= 0) {
    errors[key] = message;
  }
}

function validateZeroOrPositive(value, key, message, errors) {
  const parsed = Number(value);
  if (!String(value ?? "").trim() || !Number.isFinite(parsed) || parsed < 0) {
    errors[key] = message;
  }
}
